// Match a lab result to a Terrain marker by its test NAME, for results that carry no catalog LOINC. On real records many
// results have no LOINC and no Scrips component code (dev data check, 4 Nov: 387 of one record's 741 current results), so a
// LOINC-only Terrain saw 6 of 22 markers and lost its radar. LOINC first (use-terrain-readings reads those by code); the
// name is the second key, from a normalised alias list per marker. Pure.

/** Normalised aliases per catalog marker key. */
const ALIASES: Record<string, readonly string[]> = {
  apob: ['apob', 'apo b', 'apolipoprotein b', 'apolipoprotein b100', 'apolipoprotein b 100', 'apo b 100'],
  lpa: ['lpa', 'lp a', 'lipoprotein a', 'lipoprotein little a'],
  ldl: ['ldl', 'ldl c', 'ldl cholesterol', 'low density lipoprotein cholesterol', 'low density lipoprotein', 'cholesterol ldl', 'cholesterol in ldl'],
  hdl: ['hdl', 'hdl c', 'hdl cholesterol', 'high density lipoprotein cholesterol', 'high density lipoprotein', 'cholesterol hdl', 'cholesterol in hdl'],
  nonhdl: ['non hdl', 'non hdl c', 'non hdl cholesterol', 'cholesterol non hdl'],
  totalchol: ['cholesterol', 'total cholesterol', 'cholesterol total'],
  tg: ['tg', 'triglyceride', 'triglycerides'],
  hba1c: ['hba1c', 'hb a1c', 'a1c', 'haemoglobin a1c', 'hemoglobin a1c', 'glycated haemoglobin', 'glycated hemoglobin', 'glycosylated haemoglobin', 'glycosylated hemoglobin', 'glycohemoglobin', 'glycohaemoglobin'],
  glucose_fasting: ['fasting glucose', 'glucose fasting', 'fasting blood glucose', 'fasting plasma glucose', 'fbg', 'fbs', 'fpg'],
  hscrp: ['hscrp', 'hs crp', 'high sensitivity crp', 'crp high sensitivity', 'c reactive protein high sensitivity', 'high sensitivity c reactive protein', 'crp hs'],
  egfr: ['egfr', 'estimated gfr', 'estimated glomerular filtration rate', 'gfr estimated'],
  uacr: ['uacr', 'acr', 'albumin creatinine ratio', 'urine albumin creatinine ratio', 'microalbumin creatinine ratio', 'albumin creatinine ratio urine', 'urine acr'],
  vitd: ['vitamin d', '25 oh vitamin d', '25 hydroxyvitamin d', '25 hydroxy vitamin d', 'vitamin d total', 'vitamin d 25 hydroxy', '25 oh d', 'vitamin d 25 oh', '25 hydroxycholecalciferol', 'vitamin d total 25 hydroxycholecalciferol'],
};

/** Words that qualify a test name without changing what it measures. */
const QUALIFIERS = new Set(['serum', 'plasma', 'blood', 'whole', 'level', 'levels', 'calc', 'calculated', 'measured', 'direct', 'test', 's', 'p']);

/** Lower case, "(a)" kept as " a ", other brackets dropped, punctuation to spaces, qualifier words removed. */
export function normaliseTestName(name: string): string {
  return name
    .toLowerCase()
    .replace(/\(\s*a\s*\)/g, ' a ')
    .replace(/\([^)]*\)/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .split(' ')
    .filter((w) => w && !QUALIFIERS.has(w))
    .join(' ');
}

const BY_ALIAS: ReadonlyMap<string, string> = new Map(Object.entries(ALIASES).flatMap(([key, list]) => list.map((a) => [normaliseTestName(a), key] as const)));

/** Words after a comma that name a method or specimen, not a different test ("Hemoglobin A1c, Capillary Electrophoresis"). */
const METHOD_WORDS = new Set(['serum', 'plasma', 'blood', 'whole', 'urine', 'capillary', 'venous', 'electrophoresis', 'hplc', 'immunoassay', 'ifcc', 'ngsp', 'dcct', 'calculated', 'calc', 'direct', 'measured', 'quantitative', 'random']);

/** The catalog marker key a test name stands for, or null. The full name first; the part before the first comma only when
 *  everything after it is a method or specimen word ("Cholesterol, LDL/HDL ratio" is not total cholesterol; "Vitamin D,
 *  1,25-Dihydroxy" is not 25-OH vitamin D). */
export function markerKeyForTestName(name: string | null | undefined): string | null {
  if (!name) return null;
  const full = BY_ALIAS.get(normaliseTestName(name));
  if (full) return full;
  const comma = name.indexOf(',');
  if (comma < 0) return null;
  const rest = name
    .slice(comma + 1)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .split(' ')
    .filter(Boolean);
  if (!rest.length || !rest.every((w) => METHOD_WORDS.has(w))) return null;
  return BY_ALIAS.get(normaliseTestName(name.slice(0, comma))) ?? null;
}
